import {WorkstreamBasicWithSummary, WorkstreamMetric} from 'client';
import {CURRENT_QUARTER} from 'constant';
import {useMemo, useState} from 'react';
import {getFullWorkstreamMetrics} from 'views/workstreams/utils';

import {type MetricsGrain} from './MetricsGrainControls';
import {getDefaultMetricsTimeframe, type MetricsTimeframe} from './metricsTimeframe';
import {getMetricValueYears} from './metricsYear';
import {type MetricQuarter} from './WorkstreamMetricsQuarterTable';

export interface MetricsScorecardControls {
  metricValueYears: number[];
  selectedYear: number;
  grain: MetricsGrain;
  quarter: MetricQuarter;
  /** Picks the grain, quarter and year together (one change, not three). */
  setTimeframe: (timeframe: MetricsTimeframe) => void;
  effectiveYear: number;
  selectedPeriod: string;
}

/**
 * Shared state for the Metrics view's year + grain (Annual/Quarterly) controls.
 * Lifted out of the scorecard list so the controls can live in the page toolbar
 * while the scorecard body reads the same state. Pass `controlled` to keep the
 * timeframe somewhere else (e.g. the URL) instead of local state.
 */
export const useMetricsScorecardControls = (
  workstreams: WorkstreamBasicWithSummary[],
  controlled?: {timeframe: MetricsTimeframe; onChange: (timeframe: MetricsTimeframe) => void}
): MetricsScorecardControls => {
  const metricValueYears = useMemo(() => {
    const allMetrics: WorkstreamMetric[] = workstreams.flatMap((workstream) =>
      getFullWorkstreamMetrics(workstream.metrics)
    );
    return getMetricValueYears(allMetrics);
  }, [workstreams]);

  // Metrics view controls: an Annual/Quarterly grain toggle (defaulting to
  // Quarterly) plus a year dropdown. The year dropdown offers a fixed FY26–FY27
  // window (see `MetricsScorecardControls`), so the selected year is deliberately
  // NOT clamped to years that happen to have data — `effectiveYear` follows the
  // selection.
  const [localTimeframe, setLocalTimeframe] = useState(getDefaultMetricsTimeframe);
  const {grain, quarter, year: selectedYear} = controlled?.timeframe ?? localTimeframe;
  const setTimeframe = controlled?.onChange ?? setLocalTimeframe;
  const effectiveYear = selectedYear;
  const selectedPeriod = `${effectiveYear}Q${CURRENT_QUARTER}`;

  return {
    metricValueYears,
    selectedYear,
    grain,
    quarter,
    setTimeframe,
    effectiveYear,
    selectedPeriod,
  };
};
